import "./cart-content";

class MiniCart extends HTMLElement {
  constructor() {
    super();
    this.requestId = 1;

    // Delegated so the listeners survive the cart content being morphed
    this.addEventListener("click", (e) => {
      if (e.target.closest("[data-close]")) {
        this._close();
        return;
      }

      if (e.target.closest(".cart")) return;

      this._close();
    });

    this.handleDocumentClick = (e) => {
      // Ignore clicks on items no longer in DOM
      if (!document.body.contains(e.target)) return;

      if (!this.content?.contains(e.target)) {
        this._close();
      }
    };

    document.addEventListener("click", this.handleDocumentClick);

    this.closeEsc = (e) => {
      if (e.key === "Escape") {
        this._close();
      }
    };

    document.addEventListener("keydown", this.closeEsc);

    document.addEventListener("mini-cart:open", (e) =>
      this._open(e.detail?.trackView),
    );
    document.addEventListener("mini-cart:close", () => this._close());
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this.closeEsc);
    document.removeEventListener("click", this.handleDocumentClick);
  }

  _toggle() {
    this.hasAttribute("open") ? this._close() : this._open();
  }

  _open(trackView = false) {
    if (this.hasAttribute("open")) return;

    if (trackView) this.querySelector("cart-content")?.trackView();

    // Modals marked `stays-open-with-cart` (the shop-the-look drawer) sit
    // behind the bag and are returned to once it closes.
    document.dispatchEvent(
      new CustomEvent("markup-modal:close", {
        detail: { reason: "mini-cart" },
      }),
    );
    this._setModalsCovered(true);

    this.style.display = "block";
    window.requestAnimationFrame(() => {
      // Back to the top of the bag, so a just-added item is the first thing in
      // view however far the shopper had scrolled last time. Only settable once
      // the drawer is displayed, hence in here.
      if (this.scroller) this.scroller.scrollTop = 0;

      this.setAttribute("open", "");
      window.openFullDimmer(this);
      window.lockScroll(this);
      this.returnFocusEl = document.activeElement;
      window.trapFocus(this, this.closeButton);
    });
  }

  get content() {
    return this.querySelector(".cart");
  }

  get closeButton() {
    return this.querySelector("[data-close]");
  }

  get scroller() {
    return this.querySelector(".cart__scroll");
  }

  // Tied to the bag's own slide rather than to its focus trap, which is not
  // released until the slide-out has finished — the modal beneath would stay
  // dimmed for the whole of it.
  _setModalsCovered(covered) {
    document.dispatchEvent(
      new CustomEvent("markup-modal:cover", { detail: { covered } }),
    );
  }

  _close() {
    if (!this.hasAttribute("open")) return;

    this._setModalsCovered(false);
    this.removeAttribute("open");
    window.closeFullDimmer(this);

    const onEnd = () => {
      this.removeEventListener("transitionend", onEnd);

      if (this.hasAttribute("open")) return;
      this.style.display = null;
      window.removeTrapFocus(this.returnFocusEl, this);
      window.unlockScroll(this);
    };
    this.addEventListener("transitionend", onEnd);
  }
}

if (!customElements.get("mini-cart")) {
  customElements.define("mini-cart", MiniCart);
}
